import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { ParamKeyValuePair } from "react-router-dom/dist/dom";

import { TablePaginationConfig } from "antd";

import { CmtMissionsTable } from "@app/components/missions/CmtMissionsTable.tsx";
import { MissionPageFilters } from "@app/components/missions/filters/MissionPageFilters.tsx";
import { CmtPage } from "@app/components/page/CmtPage.tsx";
import {
  DEFAULT_PAGE,
  DEFAULT_SIZE,
} from "@app/constants/paginationConstants.ts";
import {
  CLIENT_PARAM,
  ENDED_PARAM,
  TAKIMA_EMAIL_PARAM,
} from "@app/services/api/useMissionService.ts";
import { useMissions } from "@app/services/hook/useMissions.tsx";
import { showNotification } from "@app/services/notification/showNotification.tsx";

const extractParams = (
  page: number = DEFAULT_PAGE,
  clientSearch: string | undefined = undefined,
  userEmailSearch: string | undefined = undefined,
  showEndedMissions = false,
): ParamKeyValuePair[] => [
  ["page", String(page)],
  [CLIENT_PARAM, clientSearch ?? ""],
  [TAKIMA_EMAIL_PARAM, userEmailSearch ?? ""],
  [ENDED_PARAM, showEndedMissions.toString()],
];

export const MissionsPage = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page") ?? DEFAULT_PAGE),
  );
  const [clientSearch, setClientSearch] = useState<string | undefined>(
    searchParams.get(CLIENT_PARAM) ?? undefined,
  );
  const [userEmailSearch, setUserEmailSearch] = useState<string | undefined>(
    searchParams.get(TAKIMA_EMAIL_PARAM) ?? undefined,
  );
  const [showEndedMissions, setShowEndedMissions] = useState(
    searchParams.get(ENDED_PARAM)?.toLowerCase() === "true",
  );

  const { missions, isLoading, error } = useMissions(
    currentPage,
    DEFAULT_SIZE,
    clientSearch,
    userEmailSearch,
    showEndedMissions,
  );

  useEffect(() => {
    if (error) {
      showNotification({
        type: "error",
        title: intl.formatMessage({
          id: "error.title",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "error.missions.description",
          defaultMessage: "Failed to access missions",
        }),
      });
    }
  }, [error, intl]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const newPage = pagination.current ?? DEFAULT_PAGE;
    setCurrentPage(newPage);
    setSearchParams(
      extractParams(newPage, clientSearch, userEmailSearch, showEndedMissions),
    );
  };

  const handleClientSearchChange = (value: string) => {
    setClientSearch(value);
    setCurrentPage(DEFAULT_PAGE);
    setSearchParams(
      extractParams(DEFAULT_PAGE, value, userEmailSearch, showEndedMissions),
    );
  };

  const handleUserEmailSearchChange = (value: string) => {
    setUserEmailSearch(value);
    setCurrentPage(DEFAULT_PAGE);
    setSearchParams(
      extractParams(DEFAULT_PAGE, clientSearch, value, showEndedMissions),
    );
  };

  const handleShowingEndedMissions = (value: boolean) => {
    setShowEndedMissions(value);
    setCurrentPage(DEFAULT_PAGE);
    setSearchParams(
      extractParams(DEFAULT_PAGE, clientSearch, userEmailSearch, value),
    );
  };

  const resetFilters = () => {
    setCurrentPage(DEFAULT_PAGE);
    setClientSearch(undefined);
    setUserEmailSearch(undefined);
    setShowEndedMissions(false);
    setSearchParams(extractParams());
  };

  return (
    <CmtPage
      eyebrow={
        <FormattedMessage id="missions.eyebrow" defaultMessage="Staffing" />
      }
      title={<FormattedMessage id="app.missions" defaultMessage="Missions" />}
    >
      <MissionPageFilters
        showingEndedMissions={showEndedMissions}
        clientSearch={clientSearch}
        userEmailSearch={userEmailSearch}
        handleClientSearchChange={handleClientSearchChange}
        handleUserEmailSearchChange={handleUserEmailSearchChange}
        handleShowingEndedMissions={handleShowingEndedMissions}
        reset={resetFilters}
      />
      <CmtMissionsTable
        missions={missions}
        isLoading={isLoading}
        onChange={handleTableChange}
        currentPage={currentPage}
      />
    </CmtPage>
  );
};
